// dateTimeHelpers.js
import moment from "moment-timezone";

export const timezoneLabelToIana = {
  "Pacific Time (PT)": "America/Los_Angeles",
  "Mountain Time (MT)": "America/Boise",
  "Central Time (CT)": "America/Chicago",
  "Eastern Time (ET)": "America/New_York",
  "Alaska Time (AKT)": "America/Anchorage",
  "Hawaii-Aleutian Time (HAT)": "America/Adak",
  "Hawaii-Honolulu (HST)": "Pacific/Honolulu",
  "Australian Eastern Standard Time (AEST)": "Australia/Sydney",
  "Australian Central Standard Time (ACST)": "Australia/Adelaide",
  "Australian Western Standard Time (AWST)": "Australia/Perth",
  "New Zealand Standard Time (NZST)": "Pacific/Auckland",
  "Australian Eastern Daylight Time (AEDT)": "Australia/Sydney",
  "New Zealand Daylight Time (NZDT)": "Pacific/Auckland",
};

export const ianaToTimezoneLabel = Object.entries(timezoneLabelToIana).reduce(
  (acc, [label, iana]) => {
    acc[iana] = label;
    return acc;
  },
  {}
);

export const DEFAULT_TIMEZONE_LABEL = "Pacific Time (PT)";
export const DEFAULT_TIMEZONE_IANA = "America/Los_Angeles";

export const toIanaTimeZone = (timeZone) =>
  timezoneLabelToIana[timeZone] || timeZone || DEFAULT_TIMEZONE_IANA;

export const toTimezoneLabel = (timeZone) =>
  ianaToTimezoneLabel[timeZone] || timeZone || DEFAULT_TIMEZONE_LABEL;

/**
 * Explicit timezone ownership map
 * A timezone field can control multiple date fields
 */
export const timezoneFieldToDateFields = {
  eventStartTimeZone: ["eventStartDate", "eventEndDate"],
  purchaseStartTimeZone: [
    "purchaseStartDate",
    "purchaseEndDate",
    "startFeeDate",
    "changeableUntilDate",
    "visibleOnCalendarDate",
  ],
  visibleOnCalendarTimeZone: ["visibleOnCalendarDate"],
  eagleStartTimeZone: ["eagleStartDate", "eagleEndDate"],
  checkInStartTimeZone: ["checkInStartDate", "checkInEndDate"],
  startFeeTimeZone: ["startFeeDate"],
  changeableUntilTimeZone: ["changeableUntilDate"],
};

export const getTimezoneFieldForDateField = ({
  dateField,
  ruleset,
  isLocal2,
}) => {
  if (dateField === "eventStartDate" || dateField === "eventEndDate") {
    return "eventStartTimeZone";
  }

  if (!isLocal2) {
    if (
      dateField === "purchaseStartDate" ||
      dateField === "purchaseEndDate" ||
      dateField === "startFeeDate" ||
      dateField === "changeableUntilDate"
    ) {
      return "purchaseStartTimeZone";
    }
  } else {
    if (dateField === "purchaseStartDate" || dateField === "purchaseEndDate") {
      return "purchaseStartTimeZone";
    }
  }

  if (dateField === "visibleOnCalendarDate") {
    return isLocal2 || ruleset === "Local v2.0"
      ? "visibleOnCalendarTimeZone"
      : "purchaseStartTimeZone";
  }

  if (dateField === "eagleStartDate" || dateField === "eagleEndDate") {
    return "eagleStartTimeZone";
  }

  if (dateField === "checkInStartDate" || dateField === "checkInEndDate") {
    return "checkInStartTimeZone";
  }

  return dateField.replace("Date", "TimeZone");
};

export const utcToLocalParts = (utcIsoString, timeZone) => {
  if (!utcIsoString) {
    return { date: "", time: "" };
  }

  const zoned = moment.utc(utcIsoString).tz(toIanaTimeZone(timeZone));

  return {
    date: zoned.format("MM/DD/YYYY"),
    time: zoned.format("HH:mm") === "00:00" ? "" : zoned.format("HH:mm"),
  };
};

export const utcToLocalDateTimeDisplay = (utcIsoString, timeZone) => {
  if (!utcIsoString) return "N/A";

  return moment
    .utc(utcIsoString)
    .tz(toIanaTimeZone(timeZone))
    .format("MM/DD/YYYY hh:mm A");
};

export const buildUtcFromLocalParts = ({ localDate, localTime, timeZone }) => {
  if (!localDate || !moment(localDate, "MM/DD/YYYY", true).isValid()) {
    return null;
  }

  const safeTime = localTime || "00:00";
  const formattedDate = moment(localDate, "MM/DD/YYYY").format("YYYY-MM-DD");

  return moment
    .tz(
      `${formattedDate} ${safeTime}`,
      "YYYY-MM-DD HH:mm",
      toIanaTimeZone(timeZone)
    )
    .utc()
    .toISOString();
};

export const buildInitialDateState = ({ dates, ruleset, isLocal2 }) => {
  const dateInfoData = {};
  const timeZoneData = {};

  Object.entries(dates || {}).forEach(([key, field]) => {
    const rawValue = field?.saved ?? field?.value ?? null;

    if (key.endsWith("TimeZone")) {
      timeZoneData[key] = rawValue || DEFAULT_TIMEZONE_LABEL;
    }
  });

  Object.entries(dates || {}).forEach(([key, field]) => {
    if (!key.endsWith("Date")) return;

    const rawValue = field?.saved ?? field?.value ?? null;
    const timezoneField = getTimezoneFieldForDateField({
      dateField: key,
      ruleset,
      isLocal2,
    });

    const timeZoneValue =
      timeZoneData[timezoneField] ||
      dates?.[timezoneField]?.saved ||
      dates?.[timezoneField]?.value ||
      DEFAULT_TIMEZONE_LABEL;

    const { date, time } = utcToLocalParts(rawValue, timeZoneValue);

    dateInfoData[key] = date;
    dateInfoData[key.replace("Date", "Time")] = time;
  });

  return { dateInfoData, timeZoneData };
};

export const rebuildDatesForTimezoneChange = ({
  timezoneField,
  nextTimezone,
  dateInfoData,
  ruleset,
  isLocal2,
}) => {
  const updatedUtcValues = {};

  const controlledDateFields = Object.keys(dateInfoData).filter((key) => {
    if (!key.endsWith("Date")) return false;

    const owner = getTimezoneFieldForDateField({
      dateField: key,
      ruleset,
      isLocal2,
    });

    return owner === timezoneField;
  });

  controlledDateFields.forEach((dateField) => {
    const timeField = dateField.replace("Date", "Time");
    const localDate = dateInfoData[dateField];
    const localTime = dateInfoData[timeField] || "00:00";

    const rebuiltUtc = buildUtcFromLocalParts({
      localDate,
      localTime,
      timeZone: nextTimezone,
    });

    if (rebuiltUtc) {
      updatedUtcValues[dateField] = rebuiltUtc;
    }
  });

  return updatedUtcValues;
};
